// REST client for the contract in docs/CONTRACT.md.
//
// Hand written on purpose rather than derived from apps/api's route types:
// the API is being built in parallel, so the contract file is the source of
// truth here and this module must compile and run before a single route
// exists. Every call is credentialed so the signed session cookie rides
// along, and every call throws ApiError on a non 2xx so the outbox can tell
// "retry later" from "give up".
import { API_URL } from "./config";
import type {
  AuthoredMoment,
  BudgetCategory,
  BudgetInput,
  BudgetItem,
  BudgetSummary,
  BudgetTotals,
  CalendarMonth,
  Comment,
  CreateTripInput,
  DiscoverCard,
  MeUser,
  GeocodeHit,
  GeocodeResult,
  ItineraryInput,
  ItineraryItem,
  LivePresence,
  MediaUploadResult,
  Moment,
  MomentInput,
  Place,
  PlaceEntries,
  PresenceInput,
  ProfilePatch,
  PublicUser,
  SearchResults,
  SearchType,
  SwipeDirection,
  TrackPoint,
  TrackPointInput,
  TravelStats,
  Trip,
  TripDetail,
  WishlistItem,
} from "./types";

export class ApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
  /** 4xx other than 408 and 429 will never succeed on a retry. */
  get isPermanent(): boolean {
    return this.status >= 400 && this.status < 500 && this.status !== 408 && this.status !== 429;
  }
}

/**
 * The server's own words for a rejection, when it gave any. Its 400 bodies
 * look like `{ error, issues: [{ path, message }] }` and those messages are
 * ACTIONABLE ("must fall inside the trip range, 2026-09-01 to 2026-09-05",
 * "that plan spans 2 days and this trip has room for 1"). Swallowing them for
 * a generic "could not be saved" would throw away the only part of the answer
 * a traveller can act on. A bare `{ error: "Forbidden" }` carries no issues,
 * so it returns null and the caller falls back to its own copy.
 */
export function apiIssueMessage(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  try {
    const body = JSON.parse(error.message) as {
      issues?: Array<{ message?: unknown }>;
    };
    const first = body.issues?.find((issue) => typeof issue.message === "string");
    return first ? String(first.message) : null;
  } catch {
    return null;
  }
}

function url(path: string): string {
  return `${API_URL}${path}`;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url(path), {
    ...init,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(init?.body && !(init.body instanceof FormData)
        ? { "Content-Type": "application/json" }
        : {}),
      ...init?.headers,
    },
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => "");
    throw new ApiError(res.status, detail || `${res.status} ${res.statusText}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const apiClient = {
  health(): Promise<{ ok: true }> {
    return request("/health");
  },

  me(): Promise<{ user: PublicUser | null }> {
    return request("/auth/me");
  },
  login(username: string, password: string): Promise<{ user: PublicUser }> {
    return request("/auth/login", { method: "POST", body: JSON.stringify({ username, password }) });
  },
  register(input: {
    username: string;
    password: string;
    displayName: string;
  }): Promise<{ user: PublicUser }> {
    return request("/auth/register", { method: "POST", body: JSON.stringify(input) });
  },
  logout(): Promise<{ ok: boolean }> {
    return request("/auth/logout", { method: "POST" });
  },

  /**
   * Every live traveller, globally. Deliberately NOT trip scoped: travellers
   * here are strangers with unrelated trips who happen to share a point.
   */
  async listPresence(): Promise<LivePresence[]> {
    return asArray<LivePresence>(await request<unknown>("/presence"), "presence");
  },
  publishPresence(input: PresenceInput): Promise<{ ok: boolean }> {
    return request("/presence", { method: "POST", body: JSON.stringify(input) });
  },
  clearPresence(): Promise<{ ok: boolean }> {
    return request("/presence", { method: "DELETE" });
  },

  async listExploreMoments(options?: {
    limit?: number;
    cursor?: string | null;
  }): Promise<AuthoredMoment[]> {
    const params = new URLSearchParams();
    if (options?.limit) params.set("limit", String(options.limit));
    if (options?.cursor) params.set("cursor", options.cursor);
    const query = params.size > 0 ? `?${params.toString()}` : "";
    return asArray<AuthoredMoment>(
      await request<unknown>(`/explore/moments${query}`),
      "moments",
    );
  },
  /**
   * Destination places inside a bounding box. Returns null, never throws,
   * when the route is not on this API build yet: the map then groups places
   * from the public feed with the same rule (see src/lib/places.ts), so the
   * screen works either way.
   */
  async listPlaces(bbox: [number, number, number, number], limit = 200): Promise<Place[] | null> {
    try {
      const query = `?bbox=${bbox.join(",")}&limit=${limit}`;
      return asArray<Place>(await request<unknown>(`/places${query}`), "places");
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  },
  /**
   * Address search. Called on submit or heavily debounced, never per
   * keystroke: the upstream provider's usage policy forbids autocomplete
   * style querying and the API rate limits it.
   */
  async geocode(query: string): Promise<GeocodeResult> {
    const payload = await request<unknown>(`/geocode?q=${encodeURIComponent(query)}`);
    if (Array.isArray(payload)) return { results: payload as GeocodeHit[], attribution: null };
    const record = (payload ?? {}) as Record<string, unknown>;
    return {
      results: asArray<GeocodeHit>(payload, "results"),
      attribution: typeof record.attribution === "string" ? record.attribution : null,
    };
  },

  placeEntries(placeId: string): Promise<PlaceEntries> {
    return request(`/places/${encodeURIComponent(placeId)}/entries`);
  },

  /** Idempotent server side, so a double tap can never double count. */
  likeMoment(momentId: string): Promise<{ liked: boolean; likeCount: number }> {
    return request(`/moments/${encodeURIComponent(momentId)}/like`, { method: "POST" });
  },
  unlikeMoment(momentId: string): Promise<{ liked: boolean; likeCount: number }> {
    return request(`/moments/${encodeURIComponent(momentId)}/like`, { method: "DELETE" });
  },
  async listComments(momentId: string, limit = 50): Promise<Comment[]> {
    return asArray<Comment>(
      await request<unknown>(`/moments/${encodeURIComponent(momentId)}/comments?limit=${limit}`),
      "comments",
    );
  },
  addComment(momentId: string, body: string): Promise<Comment> {
    return request(`/moments/${encodeURIComponent(momentId)}/comments`, {
      method: "POST",
      body: JSON.stringify({ body }),
    });
  },
  deleteComment(commentId: string): Promise<{ ok: boolean }> {
    return request(`/comments/${encodeURIComponent(commentId)}`, { method: "DELETE" });
  },

  async userTimeline(userId: string): Promise<AuthoredMoment[]> {
    return asArray<AuthoredMoment>(
      await request<unknown>(`/users/${encodeURIComponent(userId)}/timeline`),
      "moments",
    );
  },

  /* --------------------------------------------------- round 5 routes */

  /**
   * The traveller's own record. `null` when this API build has no
   * /users/me yet, so Profil falls back to the session user from
   * /auth/me rather than showing an error for a route it does not own.
   */
  async profile(): Promise<MeUser | null> {
    try {
      return await request<MeUser>("/users/me");
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  },
  updateMe(patch: ProfilePatch): Promise<MeUser> {
    return request("/users/me", { method: "PATCH", body: JSON.stringify(patch) });
  },
  /**
   * The server answers with the refreshed profile alongside the URL, so the
   * header and every avatar on screen update from this one round trip
   * instead of a follow up GET /users/me.
   */
  uploadAvatar(
    file: Blob,
    filename: string,
  ): Promise<{ photoUrl: string; profile?: MeUser }> {
    const form = new FormData();
    form.append("file", file, filename);
    return request("/users/me/avatar", { method: "POST", body: form });
  },

  /**
   * The Cari deck. Returns null, never throws, when the route is not on this
   * API build yet: the deck then falls back to destinations grouped from the
   * public feed, so the screen is never a dead end.
   */
  /** 30 is the server's ceiling for this route, so it is also the default. */
  async listDiscover(limit = 30): Promise<DiscoverCard[] | null> {
    try {
      return asArray<DiscoverCard>(await request<unknown>(`/discover?limit=${limit}`), "cards");
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  },
  recordSwipe(
    placeId: string,
    direction: SwipeDirection,
  ): Promise<{ recorded: boolean; wishlisted: boolean }> {
    return request("/discover/swipe", {
      method: "POST",
      body: JSON.stringify({ placeId, direction }),
    });
  },
  /** Undo. Puts the destination back in the deck. */
  undoSwipe(placeId: string): Promise<{ ok: boolean }> {
    return request(`/discover/swipe/${encodeURIComponent(placeId)}`, { method: "DELETE" });
  },

  async listWishlist(): Promise<WishlistItem[] | null> {
    try {
      return asArray<WishlistItem>(await request<unknown>("/wishlist"), "items");
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  },
  addToWishlist(placeId: string): Promise<WishlistItem> {
    return request("/wishlist", { method: "POST", body: JSON.stringify({ placeId }) });
  },
  removeFromWishlist(placeId: string): Promise<{ ok: boolean }> {
    return request(`/wishlist/${encodeURIComponent(placeId)}`, { method: "DELETE" });
  },

  /**
   * The default discovery grid, photo first. Answers with a bare array and
   * NO cursor: deep paging is `GET /search?q=`, which returns the same items
   * with a `nextCursor`. Null on 404 so the screen can fall back to the
   * public feed filtered to entries that carry media.
   */
  async exploreGrid(limit = 50): Promise<AuthoredMoment[] | null> {
    const params = new URLSearchParams({ limit: String(limit) });
    try {
      return asArray<AuthoredMoment>(
        await request<unknown>(`/explore/grid?${params.toString()}`),
        "moments",
      );
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  },

  /** Search across places, travellers and moments. Null on 404. */
  async search(options: {
    q: string;
    type?: SearchType;
    limit?: number;
    cursor?: string | null;
  }): Promise<SearchResults | null> {
    const params = new URLSearchParams({ q: options.q });
    if (options.type && options.type !== "all") params.set("type", options.type);
    if (options.limit) params.set("limit", String(options.limit));
    if (options.cursor) params.set("cursor", options.cursor);
    try {
      const payload = (await request<unknown>(`/search?${params.toString()}`)) as
        | Partial<SearchResults>
        | null;
      return {
        places: Array.isArray(payload?.places) ? payload.places : [],
        travellers: Array.isArray(payload?.travellers) ? payload.travellers : [],
        moments: Array.isArray(payload?.moments) ? payload.moments : [],
        nextCursor: payload?.nextCursor ?? null,
      };
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  },

  listTrips(): Promise<Trip[]> {
    return request("/trips");
  },
  createTrip(input: CreateTripInput): Promise<Trip> {
    return request("/trips", { method: "POST", body: JSON.stringify(input) });
  },
  getTrip(tripId: string): Promise<TripDetail> {
    return request(`/trips/${encodeURIComponent(tripId)}`);
  },

  listTrack(tripId: string, since?: string): Promise<TrackPoint[]> {
    const query = since ? `?since=${encodeURIComponent(since)}` : "";
    return request(`/trips/${encodeURIComponent(tripId)}/track${query}`);
  },
  appendTrack(tripId: string, points: TrackPointInput[]): Promise<{ accepted: number }> {
    return request(`/trips/${encodeURIComponent(tripId)}/track`, {
      method: "POST",
      body: JSON.stringify({ points }),
    });
  },

  listMoments(tripId: string): Promise<Moment[]> {
    return request(`/trips/${encodeURIComponent(tripId)}/moments`);
  },
  createMoment(tripId: string, input: MomentInput): Promise<Moment> {
    return request(`/trips/${encodeURIComponent(tripId)}/moments`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  deleteMoment(momentId: string): Promise<{ ok: boolean }> {
    return request(`/moments/${encodeURIComponent(momentId)}`, { method: "DELETE" });
  },

  uploadMedia(file: Blob, filename: string): Promise<MediaUploadResult> {
    const form = new FormData();
    form.append("file", file, filename);
    return request("/media", { method: "POST", body: form });
  },

  /** Absolute URL for a stored media object, safe to use as an img src. */
  mediaUrl(storageKey: string): string {
    return url(`/media/${encodeURIComponent(storageKey)}`);
  },

  /* ------------------------------------------ round 7: itinerary CRUD */

  /**
   * A trip's plan. Returns null, never throws, on 404: the round 7 planning
   * routes may not be on this API build yet, and the screen degrades to the
   * day scaffold it can build from the trip itself rather than showing an
   * error for a route the server does not own. Same posture the geocoder
   * fallback already uses.
   */
  async listItinerary(tripId: string): Promise<ItineraryItem[] | null> {
    try {
      return asArray<ItineraryItem>(
        await request<unknown>(`/trips/${encodeURIComponent(tripId)}/itinerary`),
        "items",
      );
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  },
  createItineraryItem(tripId: string, input: ItineraryInput): Promise<ItineraryItem> {
    return request(`/trips/${encodeURIComponent(tripId)}/itinerary`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  updateItineraryItem(id: string, patch: Partial<ItineraryInput>): Promise<ItineraryItem> {
    return request(`/itinerary/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  },
  deleteItineraryItem(id: string): Promise<{ ok: boolean }> {
    return request(`/itinerary/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
  /** Save someone else's plan. A snapshot: later edits never propagate. */
  copyItinerary(tripId: string, fromTripId: string): Promise<{ copied: number }> {
    return request(`/trips/${encodeURIComponent(tripId)}/itinerary/copy`, {
      method: "POST",
      body: JSON.stringify({ fromTripId }),
    });
  },

  /* --------------------------------------------- round 7: budget CRUD */

  /** Null on 404, same reason as the itinerary above. */
  async listBudget(tripId: string): Promise<BudgetSummary | null> {
    try {
      const payload = await request<unknown>(`/trips/${encodeURIComponent(tripId)}/budget`);
      const items = asArray<BudgetItem>(payload, "items");
      const record = (payload ?? {}) as Record<string, unknown>;
      return { items, totals: readTotals(record.totals, items) };
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  },
  createBudgetItem(tripId: string, input: BudgetInput): Promise<BudgetItem> {
    return request(`/trips/${encodeURIComponent(tripId)}/budget`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  updateBudgetItem(id: string, patch: Partial<BudgetInput>): Promise<BudgetItem> {
    return request(`/budget/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  },
  deleteBudgetItem(id: string): Promise<{ ok: boolean }> {
    return request(`/budget/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
  /**
   * CSV as a Blob rather than a bare link, because the export is behind the
   * session cookie and a plain anchor to another origin would not carry it.
   * Null on 404 so the control can say the export is unavailable instead of
   * downloading an error page named budget.csv.
   */
  async budgetCsv(tripId: string): Promise<{ blob: Blob; filename: string } | null> {
    const res = await fetch(url(`/trips/${encodeURIComponent(tripId)}/budget.csv`), {
      credentials: "include",
      headers: { Accept: "text/csv" },
    });
    if (res.status === 404) return null;
    if (!res.ok) throw new ApiError(res.status, `${res.status} ${res.statusText}`);
    // The server names the file. The client does not second guess it, and it
    // certainly does not build the CSV itself: escaping is the server's job
    // and it already does RFC 4180 with CRLF.
    const disposition = res.headers.get("content-disposition") ?? "";
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
    return { blob: await res.blob(), filename: match?.[1] ?? "budget.csv" };
  },

  /**
   * The calendar read model: which days carry a trip and how many diary
   * entries each day holds, across EVERY trip rather than only the active
   * one. No separate store, and creating a trip from a day is still the
   * existing POST /trips. Null on 404 so the calendar falls back to marking
   * the days it can see locally.
   */
  async calendar(from: string, to: string): Promise<CalendarMonth | null> {
    try {
      const payload = (await request<unknown>(
        `/calendar?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      )) as Partial<CalendarMonth> | null;
      return {
        from: payload?.from ?? from,
        to: payload?.to ?? to,
        days: Array.isArray(payload?.days) ? payload.days : [],
        trips: Array.isArray(payload?.trips) ? payload.trips : [],
      };
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  },

  /* -------------------------------------------- round 7: travel stats */

  /**
   * Derived server side from real history. Every field is nullable: a figure
   * the server cannot derive comes back null and is rendered as unavailable,
   * never as zero. Null for the whole object means this API build has no
   * /stats/me yet.
   */
  async travelStats(): Promise<TravelStats | null> {
    try {
      return readStats(await request<unknown>("/stats/me"));
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  },

  /**
   * No tripId subscribes to the PUBLIC presence topic, which is what the
   * main map needs: every live traveller, whatever trip they are on.
   */
  realtimeUrl(tripId?: string | null): string {
    const base = API_URL || globalThis.location?.origin || "";
    const wsBase = base.replace(/^http/, "ws");
    return tripId ? `${wsBase}/realtime?tripId=${encodeURIComponent(tripId)}` : `${wsBase}/realtime`;
  },
};

/**
 * Totals, per currency. When the server sends them, they win. When it sends
 * a shape this build does not know, they are recomputed from the items with
 * integer addition, still per currency: two currencies are never added into
 * one number, because the app has no exchange rate and may not invent one.
 */
function readTotals(payload: unknown, items: BudgetItem[]): BudgetTotals[] {
  const record = (payload ?? {}) as Record<string, unknown>;
  const byCurrency = record.byCurrency;
  if (Array.isArray(byCurrency)) {
    return byCurrency.map((entry) => {
      const row = entry as {
        currency?: unknown;
        totalMinor?: unknown;
        itemCount?: unknown;
        byCategory?: unknown;
      };
      const categories: Partial<Record<BudgetCategory, number>> = {};
      if (Array.isArray(row.byCategory)) {
        for (const bucket of row.byCategory as Array<{ category?: unknown; totalMinor?: unknown }>) {
          if (typeof bucket.category === "string" && typeof bucket.totalMinor === "number") {
            categories[bucket.category as BudgetCategory] = bucket.totalMinor;
          }
        }
      }
      return {
        currency: typeof row.currency === "string" ? row.currency : "",
        totalMinor: typeof row.totalMinor === "number" ? row.totalMinor : 0,
        itemCount: typeof row.itemCount === "number" ? row.itemCount : 0,
        byCategory: categories,
      };
    });
  }

  // No totals on the wire: recompute from the items with integer addition,
  // still strictly per currency. Two currencies are never added into one
  // number, because the app holds no exchange rate and may not invent one.
  const map = new Map<string, BudgetTotals>();
  for (const item of items) {
    const amount = Math.trunc(Number(item.amountMinor) || 0);
    const bucket = map.get(item.currency) ?? {
      currency: item.currency,
      totalMinor: 0,
      itemCount: 0,
      byCategory: {},
    };
    bucket.totalMinor += amount;
    bucket.itemCount += 1;
    bucket.byCategory[item.category] = (bucket.byCategory[item.category] ?? 0) + amount;
    map.set(item.currency, bucket);
  }
  return [...map.values()].sort((a, b) => a.currency.localeCompare(b.currency));
}

/**
 * Reads a stats payload without ever manufacturing a figure. Anything the
 * server omitted, sent as null, or sent as a non number stays null, and the
 * screen prints "unavailable" for it. Two spellings are accepted per field
 * because the backend is being written in parallel against the same prose.
 */
function readStats(payload: unknown): TravelStats {
  const record = (payload ?? {}) as Record<string, unknown>;
  const num = (...keys: string[]): number | null => {
    for (const key of keys) {
      const value = record[key];
      if (typeof value === "number" && Number.isFinite(value)) return value;
    }
    return null;
  };
  const km = (value: number | null): number | null => (value === null ? null : value * 1000);
  const list = (...keys: string[]): string[] | null => {
    for (const key of keys) {
      const value = record[key];
      if (Array.isArray(value)) return value.filter((item): item is string => typeof item === "string");
    }
    return null;
  };
  return {
    entries: num("entries", "entryCount", "moments"),
    destinations: num("destinations", "destinationCount", "places"),
    trips: num("trips", "tripCount"),
    daysTravelled: num("daysTravelled", "days", "dayCount"),
    regions: list("regions"),
    provinces: list("provinces"),
    // The server reports KILOMETRES. The UI formats metres, so the one
    // conversion in the product happens here rather than in three screens.
    totalDistanceM: km(num("totalDistanceKm", "distanceKm")) ?? num("totalDistanceM", "distanceM"),
    // Always null today, and correctly so: nothing in this product records a
    // home location, so there is no honest figure to derive. The screen says
    // what is missing rather than printing a confident zero.
    farthestFromHomeM: km(num("farthestFromHomeKm")) ?? num("farthestFromHomeM"),
  };
}

/**
 * Tolerant list reader. The contract says these routes answer with an array;
 * a paginated wrapper (`{ moments: [...], nextCursor }`) is the obvious way
 * for it to grow, so accept both rather than crash a screen on a shape
 * change the frontend does not own.
 */
function asArray<T>(payload: unknown, key: string): T[] {
  if (Array.isArray(payload)) return payload as T[];
  if (payload && typeof payload === "object") {
    const wrapped = (payload as Record<string, unknown>)[key];
    if (Array.isArray(wrapped)) return wrapped as T[];
    const items = (payload as Record<string, unknown>).items;
    if (Array.isArray(items)) return items as T[];
  }
  return [];
}
